import React, { useState, useEffect, useRef } from 'react';
import './liberation.css';
import Sidebar from '../../components/Sidebar';
import { useInternals } from '../../context/internalContext';
import { useAuxiliaries } from '../../context/auxiliariesContext';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Header from '../../components/header';
import useSubmitLock from '../../utils/useSubmitLock';
import SearchInternal from '../../components/SearchInternal/SearchInternal';
import VisibleAccess from '../../permissions/VisibleAccess';

const BATCH_PROGRESS_STEP_DELAY_MS = 320;
const BATCH_PROGRESS_FINAL_HOLD_MS = 700;
const BATCH_PROGRESS_HIDE_AFTER_SUCCESS_MS = 5000;

const NO_FILE_PROCESS_TYPES = ['Absorvido', 'Internação', 'Fuga', 'Recambiamento'];

const normalizeStr = (str) =>
  String(str || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

const NO_FILE_PROCESS_TYPES_NORMALIZED = NO_FILE_PROCESS_TYPES.map(normalizeStr);

const ONLY_DATE_TYPES = ['Fuga'];
const ONLY_DATE_TYPES_NORMALIZED = ONLY_DATE_TYPES.map(normalizeStr);

const NO_PROCESS_TYPES = [
  'Saída para Unidade',
  'Término do Prazo do Monitoramento',
  'Descumprimento da Medida Cautelar',
  'Decisão Judicial',
  'Inviabilidade Técnica',
];
const NO_PROCESS_TYPES_NORMALIZED = NO_PROCESS_TYPES.map(normalizeStr);

const typeShowsOnlyDate = (typeLeavingObj) =>
  ONLY_DATE_TYPES_NORMALIZED.includes(normalizeStr(typeLeavingObj?.name));

const typeSkipsFileAndProcess = (typeLeavingObj) =>
  NO_FILE_PROCESS_TYPES_NORMALIZED.includes(normalizeStr(typeLeavingObj?.name));

const typeRequiresProcess = (typeLeavingObj) => {
  if (!typeLeavingObj?.name) return false;
  if (typeSkipsFileAndProcess(typeLeavingObj)) return false;
  return !NO_PROCESS_TYPES_NORMALIZED.includes(normalizeStr(typeLeavingObj?.name));
};

const getInternalInitial = (name) => {
  const cleanName = String(name || '').trim();
  return cleanName ? cleanName.charAt(0).toUpperCase() : '?';
};

const formatCpf = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length !== 11) {
    return value || 'CPF não informado';
  }
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
};

const getInternalName = (item) => String(item?.name || item?.internal_name || '').trim();

const wait = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

const INITIAL_BATCH_PROGRESS = {
  active: false,
  total: 0,
  current: 0,
  success: 0,
  failed: 0,
  currentName: '',
};

const flattenErrorValue = (value) => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(flattenErrorValue).join(', ');
  if (value && typeof value === 'object') return Object.values(value).map(flattenErrorValue).join('; ');
  return String(value);
};

const getErrorMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.length < 500) return data;
  if (data && typeof data === 'object') {
    const raw = data.message || data.detail || data.error;
    if (raw) return flattenErrorValue(raw);
    return error.message || 'Erro interno no servidor (500)';
  }
  return error?.message || 'Erro interno no servidor (500)';
};

const Liberation = () => {
  const { id } = useParams(); // Pegando o ID do interno selecionado
  const { getInternalById, getProcessInternals, listTypeLeaving, createLeavingRecord } = useInternals();
  const { listTypeFiles } = useAuxiliaries();
  const navigate = useNavigate();

  const [internal, setInternal] = useState(null);
  const [typeFiles, setTypeFiles] = useState([]);
  const [typeLeaving, setTypeLeaving] = useState([]);
  const [processes, setProcesses] = useState([]);
  const { isSubmitting, runWithSubmitLock } = useSubmitLock();

  const [selectedTypeLeaving, setSelectedTypeLeaving] = useState(null);
  const [formData, setFormData] = useState({});
  const [selectedInternalId, setSelectedInternalId] = useState(id || '');
  const [temporaryInternals, setTemporaryInternals] = useState([]);
  const [batchProgress, setBatchProgress] = useState(INITIAL_BATCH_PROGRESS);
  const hideBatchProgressTimeoutRef = useRef(null);
  const [showMonitoringAlert, setShowMonitoringAlert] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowMonitoringAlert(false), 30000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setSelectedInternalId(id || '');
  }, [id]);

  useEffect(() => (
    () => {
      if (hideBatchProgressTimeoutRef.current) {
        clearTimeout(hideBatchProgressTimeoutRef.current);
      }
    }
  ), []);

  useEffect(() => {
    const fetchTypeFiles = async () => {
      const response = await listTypeFiles();
      if (response && Array.isArray(response.results)) {
        setTypeFiles(response.results);
      } else {
        setTypeFiles([]);
      }
    };

    const fetchTypeLeaving = async () => {
      const response = await listTypeLeaving();
      setTypeLeaving(Array.isArray(response) ? response : (response?.results || []));
    };

    fetchTypeFiles();
    fetchTypeLeaving();
  }, [listTypeFiles, listTypeLeaving]);

  useEffect(() => {
    const fetchProcesses = async () => {
      if (selectedInternalId) {
        const response = await getProcessInternals({ internal: selectedInternalId });
        setProcesses(response && Array.isArray(response.results) ? response.results : []);
        return;
      }
      setProcesses([]);
    };

    fetchProcesses();
  }, [selectedInternalId, getProcessInternals]);

  useEffect(() => {
    const fetchInternal = async () => {
      if (!selectedInternalId) {
        setInternal(null);
        return;
      }

      const response = await getInternalById(selectedInternalId);
      setInternal(response || null);
    };

    fetchInternal();
  }, [selectedInternalId, getInternalById]);

  const ensureTemporaryInternal = (selected) => {
    if (!selected?.id) return;
    setTemporaryInternals((prev) => {
      const existingIndex = prev.findIndex((item) => String(item.id) === String(selected.id));
      if (existingIndex !== -1) {
        const current = prev[existingIndex];
        const nextName = getInternalName(selected) || current.name;
        const nextCpf = selected?.cpf || current.cpf || '';

        if (nextName === current.name && nextCpf === current.cpf) {
          return prev;
        }

        const updated = [...prev];
        updated[existingIndex] = {
          ...current,
          name: nextName,
          cpf: nextCpf,
        };
        return updated;
      }

      const nextName = getInternalName(selected);
      if (!nextName) {
        return prev;
      }

      return [
        ...prev,
        {
          id: selected.id,
          name: nextName,
          cpf: selected?.cpf || '',
          file: null,
        },
      ];
    });
  };

  const handleExitTypeChange = (typeObj) => {
    const isChangingType = Boolean(selectedTypeLeaving) && selectedTypeLeaving.id !== typeObj.id;

    setSelectedTypeLeaving(typeObj);
    setFormData({});

    if (isChangingType) {
      setSelectedInternalId('');
      setInternal(null);
      setTemporaryInternals([]);
      setBatchProgress(INITIAL_BATCH_PROGRESS);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    
    setFormData({
      ...formData,
      [name]: files ? files[0] : value,
    });
  };

  const handleSelectInternal = (selected) => {
    if (!selected?.id) return;

    if (selectedTypeLeaving?.temporary) {
      ensureTemporaryInternal(selected);
      return;
    }

    setSelectedInternalId(selected.id);
    setInternal(selected);
    setFormData({});
  };

  const handleTemporaryInternalFileChange = (internalId, file) => {
    setTemporaryInternals((prev) => prev.map((item) => (
      String(item.id) === String(internalId)
        ? { ...item, file: file || null }
        : item
    )));
  };

  const removeTemporaryInternal = (internalId) => {
    setTemporaryInternals((prev) => prev.filter((item) => String(item.id) !== String(internalId)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await runWithSubmitLock(async () => {
      if (!selectedTypeLeaving) {
        toast.warning('Selecione um tipo de saída.');
        return;
      }

      if (selectedTypeLeaving.temporary) {
        if (!formData.leaving_date) {
          toast.warning('Informe a data da saída temporária.');
          return;
        }

        if (!temporaryInternals.length) {
          toast.warning('Selecione ao menos um interno para saidão.');
          return;
        }

        let successCount = 0;
        const failedInternals = [];
        const total = temporaryInternals.length;

        setBatchProgress({
          active: true,
          total,
          current: 0,
          success: 0,
          failed: 0,
          currentName: '',
        });

        for (const [index, item] of temporaryInternals.entries()) {
          const currentName = item.name || `Interno ${index + 1}`;
          setBatchProgress({
            active: true,
            total,
            current: index,
            success: successCount,
            failed: failedInternals.length,
            currentName,
          });

          const data = new FormData();
          data.append('internal', item.id);
          data.append('type_leaving', selectedTypeLeaving.id);
          data.append('leaving_date', formData.leaving_date);
          if (item.file) data.append('file', item.file);
          if (formData.type) data.append('type', formData.type);
          if (formData.process) data.append('process', formData.process);

          try {
            await createLeavingRecord(data);
            successCount += 1;
            setBatchProgress({
              active: true,
              total,
              current: index + 1,
              success: successCount,
              failed: failedInternals.length,
              currentName,
            });
          } catch (error) {
            const apiMessage = getErrorMessage(error);
            failedInternals.push({ name: item.name, message: apiMessage });
            setBatchProgress({
              active: true,
              total,
              current: index + 1,
              success: successCount,
              failed: failedInternals.length,
              currentName,
            });
          }

          await wait(BATCH_PROGRESS_STEP_DELAY_MS);
        }

        setBatchProgress({
          active: false,
          total,
          current: total,
          success: successCount,
          failed: failedInternals.length,
          currentName: '',
        });

        await wait(BATCH_PROGRESS_FINAL_HOLD_MS);

        if (!failedInternals.length && successCount > 0) {
          toast.success(`Saída temporária registrada para ${successCount} interno(s).`);
          setTemporaryInternals([]);
          setSelectedInternalId('');
          setInternal(null);
          setFormData({});
          if (hideBatchProgressTimeoutRef.current) {
            clearTimeout(hideBatchProgressTimeoutRef.current);
          }
          hideBatchProgressTimeoutRef.current = setTimeout(() => {
            setBatchProgress(INITIAL_BATCH_PROGRESS);
          }, BATCH_PROGRESS_HIDE_AFTER_SUCCESS_MS);
          return;
        }

        if (successCount > 0) {
          toast.warning(`${successCount} interno(s) registrado(s) e ${failedInternals.length} com erro.`);
        } else {
          toast.error('Nenhuma saída temporária foi registrada.');
        }

        failedInternals.forEach(({ name, message }) => {
          toast.error(`${name}: ${message}`);
        });
        return;
      }

      if (!selectedInternalId) {
        toast.warning('Selecione um interno.');
        return;
      }

      const needsFileAndType = !selectedTypeLeaving.temporary && !typeShowsOnlyDate(selectedTypeLeaving) && !typeSkipsFileAndProcess(selectedTypeLeaving);

      if (needsFileAndType && !formData.file) {
        toast.warning('Selecione o documento.');
        return;
      }

      if (needsFileAndType && !formData.type) {
        toast.warning('Selecione o tipo de documento.');
        return;
      }

      const data = new FormData();
      data.append('internal', selectedInternalId);
      data.append('type_leaving', selectedTypeLeaving.id);
      if (formData.leaving_date) data.append('leaving_date', formData.leaving_date);
      if (formData.file) data.append('file', formData.file);
      if (formData.type) data.append('type', formData.type);
      if (formData.process) data.append('process', formData.process);

      try {
        const response = await createLeavingRecord(data);
        if (response) {
          toast.success('Saída realizada com sucesso');
          navigate('/internos');
        }
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    });
  };

  useEffect(() => {
    if (selectedTypeLeaving?.temporary && internal?.id) {
      ensureTemporaryInternal(internal);
    }
  }, [selectedTypeLeaving, internal]);

  return (
    <div className="liberation-page">
      <Sidebar />
      <Header />
      <div className='container liberation-container'>
        <section className="liberation-hero">
          <div>
            <p className="liberation-hero-overline">Fluxo de saída</p>
            <h2>Registro de Saídas de Internos</h2>
            <span>
              Defina o tipo de saída, selecione o interno e finalize com os dados obrigatórios.
            </span>
          </div>
        </section>

        <form onSubmit={handleSubmit} className="liberation-form">
          <section className="liberation-block">
            <div className="liberation-block-title">
              <span>1</span>
              <div>
                <h3>Tipo de saída</h3>
                <p>Escolha o tipo para carregar os campos necessários.</p>
              </div>
            </div>

            {showMonitoringAlert && (
              <div className="liberation-monitoring-alert">
                <strong>Atenção:</strong> se o alvará ou documento de liberação mencionar MONITORAMENTO,
                selecione o tipo <strong>&quot;Saída para Monitoramento&quot;</strong>. Se o documento não
                mencionar monitoramento, utilize o tipo de saída normal (ex: Saída Temporária).
              </div>
            )}

            <div className="liberation-type-grid">
              {typeLeaving.map((option) => {
                const typeCard = (
                  <button
                    type="button"
                    className={`liberation-type-card ${selectedTypeLeaving?.id === option.id ? 'active' : ''}`}
                    onClick={() => handleExitTypeChange(option)}
                  >
                    <strong>{option.name}</strong>
                  </button>
                );

                if (normalizeStr(option.name) === normalizeStr('Saída para Unidade')) {
                  return (
                    <VisibleAccess key={option.id} compareField="groups" compareValues={['MONITORAMENTO']}>
                      {typeCard}
                    </VisibleAccess>
                  );
                }

                return <React.Fragment key={option.id}>{typeCard}</React.Fragment>;
              })}
            </div>
          </section>

          <section className="liberation-block">
            <div className="liberation-block-title">
              <span>2</span>
              <div>
                <h3>Interno selecionado</h3>
                <p>Confirme o interno antes de registrar a saída.</p>
              </div>
            </div>

            {(!id || selectedTypeLeaving?.temporary) && (
              <div className="liberation-search-inline">
                <SearchInternal onSelect={handleSelectInternal} />
              </div>
            )}

            {selectedTypeLeaving?.temporary ? (
              <div className="liberation-batch-list">
                {temporaryInternals.length === 0 ? (
                  <div className="liberation-batch-empty">
                    Nenhum interno adicionado ao saidão.
                  </div>
                ) : (
                  temporaryInternals.map((item) => (
                    <div key={item.id} className="liberation-batch-item">
                      <div className="liberation-batch-item-main">
                        <div className="liberation-internal-avatar small">
                          {getInternalInitial(item.name)}
                        </div>
                        <div className="liberation-internal-meta">
                          <strong>{item.name || 'Interno sem nome'}</strong>
                          <span>{item?.cpf ? `CPF: ${formatCpf(item.cpf)}` : 'CPF não informado'}</span>
                        </div>
                      </div>

                      <div className="liberation-batch-item-doc">
                        <label>Documento</label>
                        <input
                          id={`temporary-file-${item.id}`}
                          className="liberation-hidden-file-input"
                          type="file"
                          onChange={(event) => handleTemporaryInternalFileChange(item.id, event.target.files?.[0])}
                        />
                        <div className="liberation-file-trigger-row">
                          <label htmlFor={`temporary-file-${item.id}`} className="liberation-file-trigger">
                            Selecionar arquivo
                          </label>
                          <span className={`liberation-file-name ${item.file ? 'selected' : ''}`}>
                            {item.file?.name || 'Nenhum arquivo selecionado'}
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="liberation-remove-btn"
                        onClick={() => removeTemporaryInternal(item.id)}
                        disabled={isSubmitting}
                      >
                        Remover
                      </button>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <div className={`liberation-internal-card ${internal ? 'selected' : 'empty'}`}>
                <div className="liberation-internal-avatar">{getInternalInitial(internal?.name)}</div>
                <div className="liberation-internal-meta">
                  <strong>{internal?.name || 'Nenhum interno selecionado'}</strong>
                  <span>
                    {selectedInternalId && internal?.cpf
                      ? `CPF: ${formatCpf(internal.cpf)}`
                      : selectedInternalId
                        ? 'CPF não informado'
                        : 'Use a busca acima para selecionar um interno'}
                  </span>
                </div>
              </div>
            )}
          </section>

          {selectedTypeLeaving && (
            <section className="liberation-block">
              <div className="liberation-block-title">
                <span>3</span>
                <div>
                  <h3>Dados complementares</h3>
                  <p>Preencha somente os campos exibidos para o tipo selecionado.</p>
                </div>
              </div>

              <div className="liberation-fields-grid">
                {!selectedTypeLeaving.temporary && !typeShowsOnlyDate(selectedTypeLeaving) && (
                  <div className='liberation-field'>
                    <label>Documento</label>
                    <input
                      type="file"
                      name="file"
                      onChange={handleInputChange}
                      required={!typeSkipsFileAndProcess(selectedTypeLeaving)}
                    />
                  </div>
                )}

                {!selectedTypeLeaving.temporary && !typeShowsOnlyDate(selectedTypeLeaving) && (
                  <div className='liberation-field'>
                    <label>Tipo de documento</label>
                    <select name="type" value={formData.type || ''} onChange={handleInputChange} required={!typeSkipsFileAndProcess(selectedTypeLeaving)}>
                      <option value="">Selecione o tipo de arquivo</option>
                      {typeFiles.map((typeFile) => (
                        <option key={typeFile.id} value={typeFile.id}>{typeFile.type}</option>
                      ))}
                    </select>
                  </div>
                )}

                {!selectedTypeLeaving.temporary && typeRequiresProcess(selectedTypeLeaving) && (
                  <div className='liberation-field'>
                    <label>Processo</label>
                    <select name="process" onChange={handleInputChange} required>
                      <option value="">Selecione o processo</option>
                      {Array.isArray(processes) && processes.map((process) => (
                        <option key={process.id} value={process.id}>
                          {process.number}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className='liberation-field'>
                  <label>Data de saída</label>
                  <input
                    type="date"
                    name="leaving_date"
                    value={formData.leaving_date || ''}
                    onChange={handleInputChange}
                    required={selectedTypeLeaving.temporary}
                  />
                </div>
              </div>
            </section>
          )}

          <div className="liberation-actions">
            <button
              type="button"
              className="liberation-cancel-btn"
              onClick={() => navigate('/internos')}
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button type="submit" className='liberation-save-btn' disabled={isSubmitting}>
              {isSubmitting ? 'Enviando...' : 'Registrar saída'}
            </button>
          </div>

          {selectedTypeLeaving?.temporary && batchProgress.total > 0 && (
            <section className="liberation-progress-card">
              <div className="liberation-progress-header">
                <strong>Progresso do envio do saidão</strong>
                <span>{batchProgress.current} / {batchProgress.total}</span>
              </div>
              <div className="liberation-progress-track" aria-hidden="true">
                <div
                  className="liberation-progress-fill"
                  style={{
                    width: `${Math.round((batchProgress.current / batchProgress.total) * 100)}%`,
                  }}
                />
              </div>
              <div className="liberation-progress-meta">
                <span>Sucesso: {batchProgress.success}</span>
                <span>Erro: {batchProgress.failed}</span>
                {batchProgress.active && batchProgress.currentName ? (
                  <span>Enviando: {batchProgress.currentName}</span>
                ) : (
                  <span>Envio concluído</span>
                )}
              </div>
            </section>
          )}
        </form>

        <div className="liberation-help-text">
          A seleção do tipo de saída define automaticamente quais campos são necessários.
        </div>
      </div>
    </div>
  );
};

export default Liberation;
